import React from 'react';
import {
  InfoOutlined as InfoIcon,
  TrendingFlat as ArrowRightIcon,
} from '@mui/icons-material';
import {
  Box,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';

export const PracticeOverview: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Practice Overview
        </Typography>
        <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
        {/* Compliance Rate Card */}
        <GridItem span={2}>
          <SmallStatCard label="Compliance Rate" value="0%" />
        </GridItem>

        {/* CPT Match Rate Card */}
        <GridItem span={2}>
          <SmallStatCard label="CPT Match Rate" value="0%" />
        </GridItem>

        {/* Revenue Progress Card */}
        <GridItem span={4}>
          <ProgressStatCard
            barColor="#3b82f6" // Blue
            secondaryBarColor="#ef4444" // Red
            barValue={0}
            secondaryBarValue={0}
            stats={[
              { label: 'Total Revenue', value: '$0', dotColor: '#3b82f6' },
              { label: 'Rejected Claims', value: '$0', dotColor: '#ef4444' },
              { label: 'Unfilled Notes', value: '$0', dotColor: '#e5e7eb' },
            ]}
          />
        </GridItem>

        {/* Notes Progress Card */}
        <GridItem span={4}>
          <ProgressStatCard
            barColor="#6366f1" // Purple
            secondaryBarColor="#a5b4fc" // Light Purple
            barValue={0}
            secondaryBarValue={0}
            stats={[
              { label: 'Total Notes', value: '0', dotColor: '#6366f1' },
              { label: 'Rejected', value: '0', dotColor: '#a5b4fc' },
              { label: 'Unsigned', value: '0', dotColor: '#e5e7eb' },
            ]}
          />
        </GridItem>
      </Box>
    </Box>
  );
};

const GridItem: React.FC<{ span: number; children: React.ReactNode }> = ({ span, children }) => (
  <Box sx={{ gridColumn: `span ${span}` }}>{children}</Box>
);

const SmallStatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          alignSelf: 'flex-end',
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <ArrowRightIcon sx={{ fontSize: 16 }} />
      </Box>
    </Paper>
  );
};

interface ProgressStatCardProps {
  barColor: string;
  secondaryBarColor: string;
  barValue: number;
  secondaryBarValue: number;
  stats: { label: string; value: string; dotColor: string }[];
}

const ProgressStatCard: React.FC<ProgressStatCardProps> = ({
  barColor,
  secondaryBarColor,
  barValue,
  secondaryBarValue,
  stats,
}) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 3,
        height: '100%',
      }}
    >
      <Box sx={{ width: '100%', mb: 3, position: 'relative', height: 6, bgcolor: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${barValue}%`, bgcolor: barColor }} />
        <Box sx={{ position: 'absolute', left: `${barValue}%`, top: 0, height: '100%', width: `${secondaryBarValue}%`, bgcolor: secondaryBarColor }} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {stats.map((stat, index) => (
          <Box key={index}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stat.dotColor }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {stat.label}
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, ml: 2 }}>
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

